'use client'

import { Box, Collapse, Divider, Stack, UnstyledButton } from '@mantine/core'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import {
  createContext,
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import ChevronDownIcon from '@/assets/icons/chevron-down.svg'
import ClockBoltIcon from '@/assets/icons/clock-bolt.svg'
import ClockNavIcon from '@/assets/icons/clock-nav.svg'
import SquareGrid2x2FillIcon from '@/assets/icons/square-grid-2x2-fill.svg'
import NextBaseImage from '@/components/basic/BaseImage'
import { synthesiseFolderFilter } from '@/services/prediction/navigationTree'
import type { PredictionNavNode } from '@/services/prediction/types'

import styles from './items.module.css'
import {
  activeChildId,
  isAllNode,
  isFolderAllActive,
  isOnActivePath,
  isQuickDurationNode,
  sortByOrdering,
} from './utils'

/**
 * Accordion coordinator for one row of sibling folders (OG-7173 #3b — Kalshi
 * pattern). Holds a single `openId`: opening one folder sets it, which
 * collapses every sibling sharing the same provider. Each nesting level wraps
 * its children in a fresh provider so the constraint is "one open per level".
 * For the sidebar's single-depth folder tree this is exactly "one open at a
 * time".
 *
 * `seedId` primes the open slot from the active selection so the folder
 * you're inside starts expanded.
 */
interface NavTreeAccordionValue {
  openId: string | null
  toggle: (id: string) => void
}

const NavTreeAccordionContext = createContext<NavTreeAccordionValue | null>(null)

export function NavTreeAccordionProvider({
  seedId,
  children,
}: Readonly<{ seedId: string | null; children: ReactNode }>) {
  const [openId, setOpenId] = useState<string | null>(seedId)
  // Re-seed when the active branch changes. Sync unconditionally — including
  // when seedId becomes null so the previously open folder collapses instead
  // of staying stuck expanded.
  useEffect(() => {
    setOpenId(seedId)
  }, [seedId])
  const value = useMemo<NavTreeAccordionValue>(
    () => ({
      openId,
      toggle: (id: string) => setOpenId(prev => (prev === id ? null : id)),
    }),
    [openId],
  )
  return (
    <NavTreeAccordionContext.Provider value={value}>{children}</NavTreeAccordionContext.Provider>
  )
}

/**
 * Visual surface variant. The same tree shows up in two places: the desktop
 * sidebar (compact rail, `sidebar`) and the mobile subcategory sheet's
 * full-height list (`sheet`) — taller touch targets and primary text since
 * the rows are the only thing on screen. Structural rules live in `.row`;
 * each surface only overrides what differs (port of cdc-web's
 * `NavTreeSurface`).
 */
type NavTreeSurface = 'sidebar' | 'sheet'

/**
 * Recursive nav-tree item set for the prediction section's left rail.
 *
 * cdc-market adaptations from cdc-web's NavTree items.tsx:
 * - No URL sync: rows are buttons; `onSelect(node)` feeds the section's local
 *   selection state instead of navigating (cdc-web hands down a de-prefixed
 *   `currentPath` + router hrefs).
 * - No theme switching: dark-only, the light-mode category-icon transform is
 *   cut.
 * - `scope` nodes are still filtered out of the tree rows.
 */
interface NavTreeItemProps {
  node: PredictionNavNode
  selectedPath: string
  onSelect: (node: PredictionNavNode) => void
  /** Visual surface — see NavTreeSurface. Defaults to the desktop sidebar. */
  surface?: NavTreeSurface
  /**
   * Nesting depth, 0 at the list root. Drives the two hierarchy cues we show
   * instead of relying on font weight: depth 0 rows keep their node icon and
   * sit flush; depth ≥ 1 rows drop the icon and indent one step per level.
   * Auto-increments as NavTreeItem recurses into children, so callers only
   * set it at the root (and usually just omit it).
   */
  depth?: number
  /**
   * Group this folder's immediate children — destinations first, folders
   * second — instead of sorting them all by `ordering`. Mirrors what the
   * sidebar does at the L1 root level (destinations and folders render as two
   * separate lists). Only applies to the immediate children — descendant
   * folders still sort by plain ordering. Off by default.
   */
  partitionChildren?: boolean
  /**
   * Force the row's active highlight on regardless of path match. Set by a
   * parent folder on its All child when the folder is in an "All context"
   * (`isFolderAllActive`). Only honoured by destination rows.
   */
  forceActive?: boolean
}

/**
 * Per-depth indentation for nested rows. depth 0 sits flush (the row's own
 * `padding-inline` from CSS); each deeper level adds one `--mantine-spacing-md`
 * via a CSS variable the `.row` rule consumes. Driven by a variable rather
 * than a static class so arbitrary tree depths indent without a class per
 * level. Returns `undefined` at depth 0 so we don't emit an inline style on
 * the common top-level rows.
 */
function indentStyle(depth: number): CSSProperties | undefined {
  if (depth <= 0) return undefined
  return { '--nav-indent-depth': depth } as CSSProperties
}

export function NavTreeItem({
  node,
  selectedPath,
  onSelect,
  surface = 'sidebar',
  partitionChildren,
  depth = 0,
  forceActive,
}: Readonly<NavTreeItemProps>) {
  // `[F+D]` clickable when the folder produces a filter (own or synthesised).
  // Drives whether the header row selects the folder or just toggles it.
  const folderFilter =
    node.node_type === 'folder' && node.path ? synthesiseFolderFilter(node) : undefined
  const isClickableFolder = !!folderFilter && !!node.path
  // Keep every non-scope child. `[F+D]` folders intentionally render the
  // `slug === 'all'` row alongside the clickable header — clicking either
  // lands on the same query, but PM wants the explicit "All" entry visible
  // so users get a labelled row, not just an implicit folder-header click.
  const filteredChildren = (node.children ?? []).filter(c => c.node_type !== 'scope')
  // Either group destinations above folders (sidebar parity at L1) or fall
  // back to the plain `ordering` sort. Within each group we still honour
  // `ordering` so BE keeps control of the per-bucket sequence.
  const visibleChildren = partitionChildren
    ? [
        ...sortByOrdering(filteredChildren.filter(c => c.node_type === 'destination')),
        ...sortByOrdering(filteredChildren.filter(c => c.node_type === 'folder')),
      ]
    : sortByOrdering(filteredChildren)
  const hasChildren = visibleChildren.length > 0

  if (hasChildren) {
    return (
      <FolderItem
        node={node}
        visibleChildren={visibleChildren}
        selectedPath={selectedPath}
        onSelect={onSelect}
        surface={surface}
        clickable={isClickableFolder}
        depth={depth}
      />
    )
  }
  return (
    <DestinationItem
      node={node}
      selectedPath={selectedPath}
      onSelect={onSelect}
      surface={surface}
      depth={depth}
      forceActive={forceActive}
    />
  )
}

interface FolderItemProps {
  node: PredictionNavNode
  visibleChildren: PredictionNavNode[]
  selectedPath: string
  onSelect: (node: PredictionNavNode) => void
  surface: NavTreeSurface
  /**
   * `true` when the folder produces a usable filter (`[F+D]` per PRD §4).
   * Drives whether the header row is selectable (label button + expand
   * chevron) or a plain expand toggle.
   */
  clickable: boolean
  depth: number
}

function FolderItem({
  node,
  visibleChildren,
  selectedPath,
  onSelect,
  surface,
  clickable,
  depth,
}: Readonly<FolderItemProps>) {
  const t = useTranslations('HomePageV2.Prediction')
  // Auto-expand any folder that sits on the selection's ancestor chain so
  // users always see where they are. Other folders stay collapsed by default
  // (PRD §6.1.4 — collapsed-by-default applies to non-active branches only).
  const onActivePath = isOnActivePath(node, selectedPath)

  // Expansion is coordinated by the sibling-level accordion (one open at a
  // time, OG-7173 #3b). When rendered outside a provider fall back to
  // independent local state so the folder still expands/collapses on its own.
  const accordion = useContext(NavTreeAccordionContext)
  const [localExpanded, setLocalExpanded] = useState(onActivePath)
  useEffect(() => {
    if (!accordion && onActivePath) setLocalExpanded(true)
  }, [accordion, onActivePath])

  const expanded = accordion ? accordion.openId === node.id : localExpanded
  const toggleExpanded = () =>
    accordion ? accordion.toggle(node.id) : setLocalExpanded(prev => !prev)
  // Cascading highlight: a folder row lights up whenever the selection sits
  // anywhere inside its subtree. Exception: when this folder is in an "All
  // context" the All row owns the highlight, so the folder header yields to
  // avoid showing both lit at once.
  const allActive = isFolderAllActive(node, selectedPath)
  const isActive = onActivePath && !allActive
  const isCurrentPage = node.path === selectedPath
  const surfaceClass = surface === 'sheet' ? styles.rowSheet : undefined

  // Clickable `[F+D]` splits the row into a label button (select) and a
  // chevron toggle so the user can select the folder without forcing it open,
  // and can expand without changing the selection. Non-clickable folders
  // keep the single-button-with-chevron toggle. Both surface the BE-supplied
  // `event_count` next to the label when present.
  const header = clickable ? (
    <Box
      className={clsx(styles.row, styles.folderRow, surfaceClass, isActive && styles.rowActive)}
      style={indentStyle(depth)}
    >
      <button
        type="button"
        className={styles.folderHeaderLink}
        aria-current={isCurrentPage ? 'page' : undefined}
        onClick={() => onSelect(node)}
      >
        <RowLabel node={node} emphasis surface={surface} depth={depth} />
        <RowCount node={node} />
      </button>
      <UnstyledButton
        className={styles.folderToggle}
        onClick={e => {
          e.stopPropagation()
          toggleExpanded()
        }}
        aria-expanded={expanded}
        aria-label={
          expanded
            ? t('collapseSectionAria', { name: node.display_name })
            : t('expandSectionAria', { name: node.display_name })
        }
      >
        <ChevronDownIcon
          width={16}
          height={16}
          aria-hidden="true"
          className={clsx(styles.chevron, expanded && styles.chevronExpanded)}
        />
      </UnstyledButton>
    </Box>
  ) : (
    <UnstyledButton
      className={clsx(styles.row, styles.folderRow, surfaceClass, isActive && styles.rowActive)}
      style={indentStyle(depth)}
      onClick={toggleExpanded}
      aria-expanded={expanded}
    >
      <RowLabel node={node} emphasis surface={surface} depth={depth} />
      <RowCount node={node} />
      <ChevronDownIcon
        width={16}
        height={16}
        aria-hidden="true"
        className={clsx(styles.chevron, expanded && styles.chevronExpanded)}
      />
    </UnstyledButton>
  )

  return (
    <Box component="li" className={styles.item}>
      {header}
      <Collapse in={expanded}>
        {/* Nested children get their own accordion scope: one sub-folder open
            per level, seeded by the active branch so the selected node's path
            stays expanded. */}
        <NavTreeAccordionProvider seedId={activeChildId(visibleChildren, selectedPath)}>
          <Stack component="ul" gap="3xs" className={clsx(styles.list, styles.nestedList)}>
            {visibleChildren.map(child => (
              <NavTreeItem
                key={child.id}
                node={child}
                selectedPath={selectedPath}
                onSelect={onSelect}
                surface={surface}
                depth={depth + 1}
                // Force-highlight this folder's All row in any All context.
                // The All `DestinationItem` can't see the folder's scope
                // siblings on its own, so the folder — which owns that context
                // via `isFolderAllActive` — tells it directly.
                forceActive={allActive && isAllNode(child)}
              />
            ))}
          </Stack>
        </NavTreeAccordionProvider>
        {/* Bottom divider mirrors Tiffany's prototype — visually separates an
            expanded folder's children from the next sibling folder. */}
        <Divider mt="3xs" />
      </Collapse>
    </Box>
  )
}

interface DestinationItemProps {
  node: PredictionNavNode
  selectedPath: string
  onSelect: (node: PredictionNavNode) => void
  surface: NavTreeSurface
  depth: number
  /** Parent-driven active override — see NavTreeItemProps.forceActive. */
  forceActive?: boolean
}

function DestinationItem({
  node,
  selectedPath,
  onSelect,
  surface,
  depth,
  forceActive,
}: Readonly<DestinationItemProps>) {
  if (!node.path) return null
  // Cascading highlight: a destination row lights up whenever the selection
  // sits on it OR extends into one of its scope tabs. Without this,
  // destinations whose only children are `scope` nodes — which `NavTreeItem`
  // filters out before this component even renders — would go dark.
  //
  // `forceActive` covers the folder's All row: the owning folder sets it in
  // any All context because the All row can't see those sibling scopes from
  // its own node.
  const isActive = forceActive || isOnActivePath(node, selectedPath)
  // `aria-current="page"` stays on exact matches only so screen readers still
  // announce the deepest "you are here" unambiguously.
  const isCurrentPage = node.path === selectedPath
  const surfaceClass = surface === 'sheet' ? styles.rowSheet : undefined

  return (
    <Box component="li" className={styles.item}>
      <UnstyledButton
        className={clsx(
          styles.row,
          styles.destinationRow,
          surfaceClass,
          isActive && styles.rowActive,
        )}
        style={indentStyle(depth)}
        aria-current={isCurrentPage ? 'page' : undefined}
        onClick={() => onSelect(node)}
      >
        <RowLabel node={node} surface={surface} depth={depth} />
        <RowCount node={node} />
      </UnstyledButton>
    </Box>
  )
}

/**
 * Trailing count chip rendered next to the row label. We render it whenever
 * BE returns a numeric `event_count` on the node — destination, folder, or
 * `[F+D]` parent. `null/undefined` (BE omits the field) renders nothing so
 * legacy nodes without counts stay clean.
 */
function RowCount({ node }: Readonly<{ node: PredictionNavNode }>) {
  if (typeof node.event_count !== 'number') return null
  return <span className={styles.count}>({node.event_count})</span>
}

interface RowLabelProps {
  node: PredictionNavNode
  /** Folder rows render at semibold; destination rows at medium. */
  emphasis?: boolean
  surface: NavTreeSurface
  /** Icon shows only at depth 0; nested rows use indentation as the cue. */
  depth: number
}

/**
 * Returns the local SVG icon component to use for a node when BE's `icon_url`
 * should be overridden by a design-specified FE icon. Returns `null` when the
 * BE icon should be used as-is.
 *
 * Rules (OG-10042 / OG-10143):
 * - "All" rows → filled 2×2 grid (Figma `.square-grid-2x2-fill`)
 * - Crypto duration rows with duration ≤ 7200s (5 min / 20 min / 2 h) → clock+bolt
 * - Crypto duration rows with duration > 7200s (Daily, Weekly, One-time) → plain clock
 */
function getLocalIconOverride(node: PredictionNavNode) {
  if (isAllNode(node)) return SquareGrid2x2FillIcon
  if (node.filter?.duration) {
    return isQuickDurationNode(node) ? ClockBoltIcon : ClockNavIcon
  }
  return null
}

function RowLabel({ node, emphasis, surface, depth }: Readonly<RowLabelProps>) {
  // cdc-market is dark-only: the light-mode `light-mode__` asset transform
  // and category-icon dimming from cdc-web are cut; icons render as shipped.
  const LocalIcon = depth === 0 ? getLocalIconOverride(node) : null
  return (
    <span
      className={clsx(
        styles.label,
        emphasis && styles.labelEmphasis,
        surface === 'sheet' && styles.labelSheet,
      )}
    >
      {depth === 0 &&
        (LocalIcon ? (
          <LocalIcon
            width={16}
            height={16}
            aria-hidden="true"
            className={clsx(
              styles.icon,
              styles.localIcon,
              // The filled "All" grid reads heavier than the outlined category
              // icons beside it; dim it to carry the same visual weight.
              isAllNode(node) && styles.allIcon,
            )}
          />
        ) : node.icon_url ? (
          <NextBaseImage
            src={node.icon_url}
            alt=""
            aria-hidden="true"
            width={16}
            height={16}
            unoptimized
            fallback={
              // Icon-less rows still reserve the 16px slot so their titles
              // line up with icon-bearing siblings.
              <span aria-hidden="true" className={styles.icon} />
            }
            className={styles.icon}
          />
        ) : (
          // Icon-less top-level rows still reserve the icon slot so their
          // titles line up with icon-bearing siblings (e.g. Politics aligns
          // with Sports). Nested rows skip this — indentation is their cue.
          <span aria-hidden="true" data-testid="node-icon-placeholder" className={styles.icon} />
        ))}
      <span>{node.display_name}</span>
    </span>
  )
}
